/**
 * Where a freshly logged-in user belongs on this dashboard.
 *
 * Each dashboard's login stays on that dashboard: this one lands staff on its
 * own management area, and v2's login lands them on v2's (`postLoginRoute` in
 * `frontend-v2/src/lib/utils/managementAccess.ts`). Nothing forwards between
 * the two on its own — crossing over is a deliberate act via the navbar banner.
 *
 * Only the login transition uses this. Visiting `/` later is left alone: unlike
 * v2, where `/` is the login screen, here it is a real page, and bouncing staff
 * off it would take away the only way to reach their own dashboard.
 */

/** The management page this dashboard opens on — same tool v2 defaults to. */
export const MANAGEMENT_DEFAULT_PATH = '/management/impersonate';

/**
 * Reads the server's IAM-native `isRetracedStaff` flag — computed from actual
 * IAM membership, independent of which company the session happens to be
 * scoped to.
 *
 * There is deliberately no role-string fallback. A role value can be
 * company-scoped (the server fills `role` from the company membership when the
 * session is scoped to one), so prefix-matching it made "is this person staff"
 * depend on which company they were last looking at. The flag has no such
 * ambiguity, and every payload this is called with now carries it.
 */
export function isRetracedStaff(
  user: { isRetracedStaff?: boolean } | null | undefined,
): boolean {
  return Boolean(user?.isRetracedStaff);
}

export function postLoginPath(
  user: { isRetracedStaff?: boolean } | null | undefined,
): string {
  return isRetracedStaff(user) ? MANAGEMENT_DEFAULT_PATH : '/';
}

/**
 * Whether a path belongs to the internal management area.
 *
 * Used to keep management out of the adoption metrics: `frontend_v2_*` RUM
 * actions measure whether *customers* are moving to the new dashboard, and
 * staff crossing between two internal tool sets is not that. Counting their
 * clicks would inflate every number built on those actions — the same reason
 * the automatic forward was never folded into `frontend_v2_try_now_clicked`.
 */
export function isManagementPath(pathname: string): boolean {
  return pathname.startsWith('/management');
}
